import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft, FolderOpen, Sparkles } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Avatar, Badge } from "../primitives";
import { Shimmer } from "../StatCard";
import { EmptyState } from "../DataTable";
import { SourceGap, fmtMins } from "./sourceUi";
import { DaySpark, HoursByDay, RollupBars, HourStrip, SplitBar } from "./charts";
import {
  DayHeadline, TakeawayList, StretchAnalysis, FullWorkLog,
  WorkLogSkeleton, WorkLogEmpty, flattenStretches,
} from "./WorkLog";
import { DashAntd } from "../DashAntd";
import { SignalList } from "./signals";
import { MemberInProject, ContributorList } from "./MemberInProject";
import * as api from "./drilldownApi";

/**
 * DrilldownDrawer — ONE drill-down for people and projects, in Brain and Classic.
 *
 * Two tiers on a single mounted node:
 *   L1  the digest, led by WHAT THE PERSON WORKED ON: the day in one line, then
 *       one compact row per stretch of work. Underneath it, where the effort
 *       went, the read, and the signals. The metrics are context for the work,
 *       not the headline.
 *   L2  the same drawer WIDENED — the L1 column stays as a context rail and the
 *       detail pane fades in beside it. Nothing unmounts, so it reads as zoom
 *       rather than navigation, and Esc steps 2 → 1 → closed.
 *
 * Clicking one takeaway opens THAT stretch's analysis. The button at the bottom
 * of the rail opens the whole log. They are different destinations on purpose.
 *
 * Both tiers read every source we collect, but HOW activity reached us is
 * internal plumbing and is never surfaced. Where a figure genuinely cannot be
 * produced for a window, the drawer says the figure is unavailable — it never
 * renders 0, and it never explains itself in terms of our collection pipeline.
 *
 * `subject`: { kind: 'person'|'project', id, name, position?, avatar? }
 */

const L1_WIDTH = 470;

export function DrilldownDrawer({ open, subject, range, people = [], onClose, onNavigate }) {
  const t = useDashTheme();
  const [level, setLevel] = React.useState(1);
  const [tab, setTab] = React.useState(null);

  const [dossier, setDossier] = React.useState(null);
  const [read, setRead] = React.useState(null);
  const [related, setRelated] = React.useState(null); // person: projects · project: members
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [readErr, setReadErr] = React.useState(null);

  /* The written work log. Separate request, separate failure: the drawer is
     still worth opening with metrics and no narrative, and vice versa. */
  const [worklog, setWorklog] = React.useState(null);
  const [worklogLoading, setWorklogLoading] = React.useState(true);
  const [worklogErr, setWorklogErr] = React.useState(null);
  /* Which stretch is open in L2. null = the whole log. */
  const [focus, setFocus] = React.useState(null);
  /* Project drawer only — which contributor is drilled into. Third tier:
     project → its people → what THAT person did on THIS project. */
  const [member, setMember] = React.useState(null);

  const isProject = subject?.kind === "project";
  const key = subject ? `${subject.kind}:${subject.id}` : null;

  /* Reset tier whenever the subject changes — including a hop from inside the
     drawer, which swaps `subject` without ever closing. */
  React.useEffect(() => {
    if (!open) return;
    setLevel(1);
    setTab(null);
    setFocus(null);
    setMember(null);
  }, [open, key]);

  React.useEffect(() => {
    if (!open || !subject?.id) return undefined;
    const ac = new AbortController();
    let alive = true;

    setLoading(true); setError(null); setReadErr(null);
    setDossier(null); setRead(null); setRelated(null);

    (async () => {
      try {
        if (isProject) {
          const data = await api.projectMembers(subject.id, range, { signal: ac.signal });
          if (!alive) return;
          setRelated(data);
          // No project narrator exists server-side yet, so the project read and
          // takeaways are derived in code from these same figures. Deterministic,
          // free, and every sentence quotes a number that is on screen.
          setRead({ sentences: api.projectRead(data, subject.name), deterministic: true });
        } else {
          const d = await api.personDossier(subject.id, range, { signal: ac.signal });
          if (!alive) return;
          setDossier(d);
          // The read is a separate model call — a failure there must not take
          // the whole drawer down, so it is caught on its own.
          api.personRead(subject.id, range, { signal: ac.signal })
            .then((r) => { if (alive) setRead(r); })
            .catch((e) => { if (alive) setReadErr(e.message || "The read could not be generated."); });
        }
      } catch (e) {
        if (alive && e.name !== "CanceledError" && e.name !== "AbortError") {
          setError(e.message || "Could not load this drill-down.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; ac.abort(); };
  }, [open, key, range, isProject, subject?.id, subject?.name]);

  /* The work log — people only. A project's written narrative would be a
     different aggregation (many people, one project) and does not exist yet;
     rendering an empty one there would imply it should. */
  React.useEffect(() => {
    if (!open || isProject || !subject?.id) return undefined;
    const ac = new AbortController();
    let alive = true;

    setWorklogLoading(true); setWorklogErr(null); setWorklog(null);
    api.personWorklog(subject.id, range, { signal: ac.signal })
      .then((w) => { if (alive) setWorklog(w); })
      .catch((e) => {
        if (alive && e.name !== "CanceledError" && e.name !== "AbortError") {
          setWorklogErr(e.message || "The work log could not be loaded.");
        }
      })
      .finally(() => { if (alive) setWorklogLoading(false); });

    return () => { alive = false; ac.abort(); };
  }, [open, isProject, subject?.id, range]);

  const stretches = React.useMemo(() => flattenStretches(worklog), [worklog]);

  /* Hop to another subject WITHOUT closing — project → contributor → that
     person's own drill-down. The parent owns `subject`, so it owns the hop; if
     it did not pass a handler the affordance is simply not offered. */
  const onHop = React.useCallback((person) => {
    if (!onNavigate || !person?.id) return;
    onNavigate({ kind: "person", id: String(person.id), name: person.name });
  }, [onNavigate]);

  /* Opening a takeaway is always a jump to L2 focused on that stretch. */
  const openStretch = React.useCallback((idx) => {
    setFocus(idx);
    setTab("worklog");
    setLevel(2);
  }, []);
  const openFullLog = React.useCallback(() => {
    setFocus(null);
    setTab("worklog");
    setLevel(2);
  }, []);

  /* Level 2 for a person needs the project split; fetched lazily so opening L1
     never pays for it. */
  React.useEffect(() => {
    if (!open || isProject || level !== 2 || related || !subject?.id) return undefined;
    const ac = new AbortController();
    let alive = true;
    api.personProjects(subject.id, range, { signal: ac.signal })
      .then((r) => { if (alive) setRelated(r); })
      .catch(() => { /* the Projects tab falls back to the dossier's own split */ });
    return () => { alive = false; ac.abort(); };
  }, [open, isProject, level, related, subject?.id, range]);

  const close = React.useCallback(() => { setLevel(1); setFocus(null); setMember(null); onClose?.(); }, [onClose]);
  /* Esc unwinds one step at a time: one stretch → the whole log → the digest →
     closed. Collapsing straight to closed from a focused stretch would throw
     away two levels of the reader's place. */
  const stepBack = React.useCallback(() => {
    if (level === 2 && focus !== null) { setFocus(null); return; }
    if (level === 2 && member) { setMember(null); return; }
    if (level === 2) { setLevel(1); return; }
    close();
  }, [level, focus, member, close]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") stepBack(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, stepBack]);

  const width = level === 2 ? "min(1180px, 96vw)" : `min(${L1_WIDTH}px, 100vw)`;

  /* The drawer portals to document.body, so it sits OUTSIDE every in-tree
     ConfigProvider — it has to carry its own or its antd bits (the hour chart,
     the contributor drill-down) render in the legacy palette. */
  return createPortal(
    <DashAntd>
    <AnimatePresence>
      {open && subject && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={stepBack}
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(8,10,18,0.5)", backdropFilter: "blur(3px)" }}
          />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0, width }} exit={{ x: "100%" }}
            transition={t.motion.spring.soft}
            style={{
              position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 9999, width,
              background: t.c.surface, borderLeft: `1px solid ${t.c.border}`, boxShadow: t.shadow.lg,
              display: "flex", flexDirection: "column", fontFamily: t.font.family,
            }}
          >
            <Header
              t={t} subject={subject} isProject={isProject}
              dossier={dossier} related={related} level={level} focus={focus} member={member}
              onClose={close}
            />

            <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
              {/* Level 1 — always mounted. In L2 it becomes the context rail. */}
              <div style={{
                width: `min(${L1_WIDTH}px, 100vw)`, flexShrink: 0, overflowY: "auto",
                padding: `${t.s[4]}px ${t.s[5]}px ${t.s[7]}px`,
                display: "flex", flexDirection: "column", gap: t.s[4],
                borderRight: level === 2 ? `1px solid ${t.c.border}` : "none",
                background: level === 2 ? t.c.surfaceMuted : t.c.surface,
              }}>
                {loading ? (
                  <L1Skeleton t={t} />
                ) : error ? (
                  <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load" description={error} />
                ) : (
                  <Level1
                    t={t} isProject={isProject} subject={subject}
                    dossier={dossier} related={related} read={read} readErr={readErr}
                    worklog={worklog} worklogLoading={worklogLoading} worklogErr={worklogErr}
                    focus={focus}
                    level={level}
                    onPickStretch={openStretch}
                    onFullLog={openFullLog}
                    onCollapse={() => setLevel(1)}
                  />
                )}
              </div>

              {/* Level 2 — same drawer, just wider. */}
              <motion.div
                animate={{ opacity: level === 2 ? 1 : 0 }}
                transition={{ duration: 0.26, delay: level === 2 ? 0.12 : 0 }}
                style={{ flex: 1, minWidth: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
              >
                {level === 2 && (
                  <Level2
                    t={t} isProject={isProject} subject={subject}
                    dossier={dossier} related={related} people={people} range={range}
                    worklog={worklog} worklogLoading={worklogLoading} worklogErr={worklogErr}
                    stretches={stretches} focus={focus} setFocus={setFocus}
                    member={member} setMember={setMember} onOpenPerson={onNavigate ? onHop : null}
                    tab={tab} setTab={setTab}
                  />
                )}
              </motion.div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </DashAntd>,
    document.body
  );
}

/* ── header ──────────────────────────────────────────────────────────────── */
function Header({ t, subject, isProject, dossier, related, level, focus, member, onClose }) {
  const sub = isProject
    ? `${related?.memberHours?.length ?? 0} contributor${related?.memberHours?.length === 1 ? "" : "s"}`
    : dossier?.identity?.position || subject.position || "";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: `${t.s[4]}px ${t.s[5]}px ${t.s[3]}px`,
      borderBottom: `1px solid ${t.c.border}`, flexShrink: 0,
    }}>
      {isProject ? (
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 42, height: 42, borderRadius: t.r.md,
          background: t.c.primarySoft, color: t.c.primary, flexShrink: 0,
        }}>
          <FolderOpen size={19} />
        </span>
      ) : (
        <Avatar user={dossier?.identity || subject} name={subject.name} size={42} />
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: t.font.size.xl, fontWeight: 600, letterSpacing: "-0.2px", lineHeight: 1.25,
          color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {dossier?.identity?.name || subject.name}
        </div>
        <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
        </div>
      </div>
      {level === 2 && (
        <Badge tone="brand">{focus !== null ? "One takeaway" : member ? "One contributor" : "In depth"}</Badge>
      )}
      <button
        onClick={onClose} aria-label="Close"
        style={{
          width: 32, height: 32, display: "inline-flex", alignItems: "center", justifyContent: "center",
          borderRadius: t.r.md, border: "none", background: t.c.surfaceMuted,
          color: t.c.textSecondary, cursor: "pointer", flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}

/* ── LEVEL 1 ─────────────────────────────────────────────────────────────── */
function Level1({
  t, isProject, subject, dossier, related, read, readErr,
  worklog, worklogLoading, worklogErr, focus,
  level, onPickStretch, onFullLog, onCollapse,
}) {
  const onExpand = onFullLog;
  const m = dossier?.metrics;
  const totals = related?.projectTotals;

  const trackedMinutes = isProject
    ? Math.round((Number(totals?.totalHours) || 0) * 60)
    : (m?.trackedMinutes || 0);
  const idleMinutes = isProject
    ? (Number(totals?.totalIdleTimeMinutes) || 0)
    : (m?.idleMinutes || 0);
  const activeMinutes = Math.max(0, trackedMinutes - idleMinutes);
  const sources = isProject ? totals?.sources : m?.sources;
  const delta = m?.comparison?.trackedMinutesDeltaPct;

  /* Projects keep their computed takeaways. People get the interpreted signals —
     grouped by whether the reader must act, and carrying the "so what" the raw
     measurement never had. See signals.jsx. */
  const projectTakeaways = isProject ? api.projectTakeaways(related) : [];

  const daySeries = m?.daySeries || [];
  const hasWork = !isProject && (worklog?.coverage?.stretches || 0) > 0;

  return (
    <>
      {/* score at a glance + the source split */}
      <div style={{
        display: "grid", gridTemplateColumns: "1fr auto", alignItems: "end", gap: 10,
        padding: `${t.s[4]}px ${t.s[4]}px`, borderRadius: t.r.md,
        background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
      }}>
        <div>
          <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginBottom: 5 }}>
            Tracked · {m?.period?.label || "this period"}
          </div>
          <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.8px", fontVariantNumeric: "tabular-nums", lineHeight: 1, color: t.c.text }}>
            {fmtMins(trackedMinutes)}
          </div>
        </div>
        {Number.isFinite(delta) && (
          <Badge tone={delta >= 0 ? "positive" : "negative"}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(Math.round(delta))}%
          </Badge>
        )}

        <div style={{ gridColumn: "1/-1", marginTop: 12 }}>
          <SplitBar activeMinutes={activeMinutes} idleMinutes={idleMinutes} height={6} />
        </div>
        <div style={{ gridColumn: "1/-1", display: "flex", gap: 14, fontSize: 11, color: t.c.textTertiary, marginTop: 7, flexWrap: "wrap" }}>
          <span><Dot c={t.status.positive} />Active <b style={{ color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtMins(activeMinutes)}</b></span>
          <span><Dot c={t.status.warning} />Idle <b style={{ color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{fmtMins(idleMinutes)}</b></span>
        </div>

        {daySeries.length > 1 && (
          <div style={{ gridColumn: "1/-1", marginTop: 12 }}>
            <DaySpark series={daySeries} height={34} />
          </div>
        )}

      </div>

      {/* ═══ WHAT THEY WORKED ON — the lead. Everything below is context. ═══ */}
      {!isProject && (
        <div>
          <SecLabel t={t} hint={worklog?.coverage?.label}>
            What {(subject.name || "they").split(" ")[0]} worked on
          </SecLabel>

          {worklogLoading ? (
            <WorkLogSkeleton />
          ) : worklogErr ? (
            <div style={{
              padding: "12px 13px", borderRadius: t.r.md, background: t.status.negativeSoft,
              fontSize: 12.5, lineHeight: 1.55, color: t.c.text,
            }}>
              {worklogErr}
            </div>
          ) : hasWork ? (
            <>
              <DayHeadline t={t} worklog={worklog} />
              <TakeawayList worklog={worklog} focusIndex={focus} onPick={onPickStretch} />
            </>
          ) : (
            <WorkLogEmpty t={t} worklog={worklog} name={subject.name} />
          )}
        </div>
      )}

      {/* where the effort went — narrative-side, distinct from the tracker split */}
      {hasWork && worklog.rollup?.length > 0 && (
        <div>
          <SecLabel t={t} hint="rolled up from the stretches above">Where the effort went</SecLabel>
          <RollupBars rows={worklog.rollup} />
        </div>
      )}

      {/* the read */}
      <div style={{
        position: "relative", padding: `${t.s[4]}px ${t.s[4]}px ${t.s[4]}px 17px`,
        borderRadius: t.r.md, background: t.c.aiSoft, boxShadow: `0 0 0 1px ${t.c.aiBorder}`,
      }}>
        <span style={{ position: "absolute", left: 0, top: 10, bottom: 10, width: 2, borderRadius: 2, background: t.c.ai }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase", color: t.c.ai }}>
            <Sparkles size={12} /> The read
          </span>
          {read?.deterministic && (
            <span style={{ fontSize: 10, color: t.c.textTertiary }}>computed</span>
          )}
        </div>
        {read?.sentences?.length ? (
          read.sentences.map((s, i) => (
            <p key={i} style={{ margin: `0 0 ${i === read.sentences.length - 1 ? 0 : 9}px`, fontSize: 13.5, lineHeight: 1.6, color: t.c.text }}>{s}</p>
          ))
        ) : readErr ? (
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: t.c.textTertiary }}>{readErr}</p>
        ) : (
          <>
            <Shimmer w="100%" h={12} /><div style={{ height: 7 }} />
            <Shimmer w="92%" h={12} /><div style={{ height: 7 }} />
            <Shimmer w="76%" h={12} />
          </>
        )}
      </div>

      {/* What a manager should actually know — deliberately BELOW the work log,
          because what someone DID outranks which thresholds they crossed. */}
      {!isProject && (dossier?.signals || []).length > 0 && (
        <div>
          <SecLabel t={t} hint="grouped by what to do about it">
            What you should know
          </SecLabel>
          <SignalList dossier={dossier} />
        </div>
      )}

      {isProject && projectTakeaways.length > 0 && (
        <div>
          <SecLabel t={t}>Key takeaways</SecLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {projectTakeaways.map((k, i) => (
              <button
                key={i} onClick={onExpand} title="Open the detail behind this"
                style={{
                  display: "flex", gap: 10, padding: "10px 12px", borderRadius: t.r.md,
                  background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`, border: "none",
                  cursor: "pointer", textAlign: "left", fontFamily: t.font.family,
                }}
              >
                <span style={{ width: 3, borderRadius: 2, flexShrink: 0, background: sevColor(t, k.sev) }} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: t.c.text }}>{k.title}</span>
                  {k.detail && <span style={{ display: "block", fontSize: 12, color: t.c.textTertiary, lineHeight: 1.5, marginTop: 3 }}>{k.detail}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* related list */}
      <RelatedList t={t} isProject={isProject} dossier={dossier} related={related} />

      {/* honest gap: agent time has no sessions */}
      {sources && sources.sessionsCoverAllTime === false && (
        <SourceGap>
          {sources.primary === "agent"
            ? "Session-level detail is not available for this window. The hours are real — they simply were not captured as start/stop sessions."
            : "Session counts and average session cover only part of this window. The remaining hours are real, but carry no session detail."}
        </SourceGap>
      )}

      {/* The CTA is always the WHOLE log — a single stretch is reached by
          clicking its own row, never by this button. */}
      <button
        onClick={level === 2 && focus === null ? onCollapse : onExpand}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          width: "100%", padding: "12px 14px", borderRadius: t.r.md, cursor: "pointer",
          border: `1px solid ${t.c.primary}`,
          background: level === 2 && focus === null ? "transparent" : t.c.primary,
          color: level === 2 && focus === null ? t.c.primary : "#fff",
          fontSize: 13.5, fontWeight: 600, fontFamily: t.font.family,
        }}
      >
        {level === 2 && focus === null ? (
          <><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><ChevronLeft size={15} /> Back to the digest</span><span style={{ fontSize: 11, opacity: 0.8 }}>Esc</span></>
        ) : (
          <>
            <span>{hasWork ? "Open the full work log" : "View full analysis"}</span>
            <ChevronRight size={15} />
          </>
        )}
      </button>
    </>
  );
}

function RelatedList({ t, isProject, dossier, related }) {
  const rows = isProject
    ? (related?.memberHours || []).slice(0, 5).map((m) => ({
      id: String(m.userId),
      name: api.memberName(m),
      value: fmtMins(Math.round((Number(m.totalHours) || 0) * 60)),
      pct: 0,
      source: m.source || "app",
    }))
    : (dossier?.projects || []).slice(0, 4).map((p) => ({
      id: p.projectId,
      name: p.name,
      value: fmtMins(p.trackedMinutes),
      pct: Math.round((Number(p.share) || 0) * 100),
      source: p.source || "app",
    }));

  if (!rows.length) return null;

  const max = isProject
    ? Math.max(...(related?.memberHours || []).map((m) => Number(m.totalHours) || 0), 1)
    : 100;

  return (
    <div>
      <SecLabel t={t}>{isProject ? "Who worked on it" : "Where the time went"}</SecLabel>
      {rows.map((r, i) => {
        const pct = isProject
          ? Math.round(((Number(related.memberHours[i]?.totalHours) || 0) / max) * 100)
          : r.pct;
        return (
          <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 2px" }}>
            <span style={{
              fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, color: t.c.text,
              display: "flex", alignItems: "center", gap: 7,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</span>
            </span>
            <span style={{ width: 70, height: 5, borderRadius: 99, background: t.c.surfaceMuted, overflow: "hidden", flexShrink: 0 }}>
              <span style={{ display: "block", height: "100%", width: `${Math.max(2, pct)}%`, background: t.c.primary }} />
            </span>
            <span style={{ fontSize: 12, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>{r.value}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── LEVEL 2 ─────────────────────────────────────────────────────────────── */
const PersonBrain = React.lazy(() => import("../../screens/brain/person/PersonBrain"));

function Level2({
  t, isProject, subject, dossier, related, people, range,
  worklog, worklogLoading, worklogErr, stretches, focus, setFocus,
  member, setMember, onOpenPerson,
  tab, setTab,
}) {
  const tabs = isProject
    ? [{ k: "people", l: "People" }, { k: "hours", l: "Hours" }]
    : [
      // The work log leads here too — it is the answer to the question the
      // drawer is opened for. Hours and Sessions are the evidence beneath it.
      { k: "worklog", l: "Work log" },
      { k: "hours", l: "Hours" },
      { k: "timeline", l: "Timeline" },
      { k: "projects", l: "Projects" },
      // TABS_PARKED (2026-08-30, user): Sessions and Brain tabs hidden from the
      // strip. Their panels (SessionsTab, the Brain chat) are untouched below —
      // restore the two lines to bring them back.
      // { k: "sessions", l: "Sessions" },
      // { k: "brain", l: "Brain" },
    ];
  const active = tab && tabs.some((x) => x.k === tab) ? tab : tabs[0].k;

  return (
    <>
      <div style={{ display: "flex", gap: 2, padding: `0 ${t.s[5]}px`, borderBottom: `1px solid ${t.c.border}`, flexShrink: 0, overflowX: "auto" }}>
        {tabs.map((x) => (
          <button
            key={x.k} onClick={() => setTab(x.k)}
            style={{
              position: "relative", padding: "13px 12px", fontSize: 13, fontWeight: 500,
              color: x.k === active ? t.c.text : t.c.textTertiary,
              background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap",
              fontFamily: t.font.family,
            }}
          >
            {x.l}
            {x.k === active && (
              <motion.span
                layoutId="tgDrillTab"
                style={{ position: "absolute", left: 8, right: 8, bottom: -1, height: 2, background: t.c.primary, borderRadius: 2 }}
              />
            )}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: t.s[5] }}>
        {active === "worklog" && (
          worklogLoading ? <WorkLogSkeleton />
            : worklogErr ? <EmptyState icon={<FolderOpen size={22} />} title="Couldn’t load the work log" description={worklogErr} />
              : focus !== null && stretches[focus] ? (
                <StretchAnalysis
                  userId={subject?.kind === "person" ? subject.id : (stretches[focus]?.userId || null)}
                  stretch={stretches[focus]}
                  index={focus}
                  total={stretches.length}
                  timeline={dossier?.timeline}
                  onBack={() => setFocus(null)}
                  onJump={setFocus}
                  onFullLog={() => setFocus(null)}
                />
              ) : (
                <FullWorkLog worklog={worklog} onPick={setFocus} />
              )
        )}
        {active === "hours" && <HoursTab t={t} isProject={isProject} dossier={dossier} related={related} />}
        {active === "timeline" && <TimelineTab t={t} dossier={dossier} />}
        {active === "projects" && <ProjectsTab t={t} dossier={dossier} related={related} />}
        {active === "sessions" && <SessionsTab t={t} dossier={dossier} subject={subject} />}
        {active === "people" && (
          member ? (
            <MemberInProject
              member={member}
              project={{ id: subject.id, name: subject.name }}
              range={range}
              onBack={() => setMember(null)}
              onOpenPerson={onOpenPerson}
            />
          ) : (
            <PeopleTab t={t} related={related} onPick={setMember} />
          )
        )}
        {active === "brain" && (
          <React.Suspense fallback={<Shimmer w="100%" h={220} />}>
            <PersonBrain
              person={{ id: subject.id, name: subject.name, ...(dossier?.identity || {}) }}
              range={range}
              people={people}
              onClose={() => {}}
            />
          </React.Suspense>
        )}
      </div>
    </>
  );
}

function HoursTab({ t, isProject, dossier, related }) {
  const series = dossier?.metrics?.daySeries || [];

  if (isProject) {
    // The project endpoint has no per-day series; the honest move is to show the
    // totals it DOES return rather than invent a shape it never produced.
    const totals = related?.projectTotals || {};
    return (
      <>
        <H3 t={t}>Totals</H3>
        <Sub t={t}>Aggregate figures for the selected range.</Sub>
        <KV t={t} items={[
          ["Tracked", fmtMins(Math.round((Number(totals.totalHours) || 0) * 60))],
          ["Active", totals.totalActiveTimeFormatted || "0h 0m"],
          ["Idle", totals.totalIdleTimeFormatted || "0h 0m"],
          ["Sessions", totals.sources?.sessionsCoverAllTime === false ? `${totals.totalSessions || 0} (app only)` : (totals.totalSessions || 0)],
          ["Contributors", totals.activeMembers ?? (related?.memberHours?.length || 0)],
        ]} />
        {totals.sources?.sessionsCoverAllTime === false && (
          <div style={{ marginTop: t.s[5] }}>
            <SourceGap>
              Session totals cover only {totals.sources.app?.formatted || "0h 0m"} of this window. The remaining
              {" "}{totals.sources.agent?.formatted} is real tracked time with no session detail attached.
            </SourceGap>
          </div>
        )}
      </>
    );
  }

  if (!series.length) return <EmptyState icon={<FolderOpen size={22} />} title="No daily data" description="No tracked time in this window." />;

  return (
    <>
      <H3 t={t}>Hours by day</H3>
      <Sub t={t}>Hover a bar for the exact figure and which tracker recorded it.</Sub>
      <HoursByDay series={series} />
      <div style={{ marginTop: t.s[6] }}>
        <KV t={t} items={[
          ["Average / active day", fmtMins(dossier?.metrics?.avgMinutesPerActiveDay || 0)],
          ["Active days", dossier?.metrics?.activeDays ?? 0],
          ["Sessions", dossier?.metrics?.sources?.sessionsCoverAllTime === false ? `${dossier?.metrics?.sessionCount || 0} (app only)` : (dossier?.metrics?.sessionCount || 0)],
          ["Longest session", fmtMins(dossier?.metrics?.longestSessionMinutes || 0)],
        ]} />
      </div>
    </>
  );
}

function TimelineTab({ t, dossier }) {
  const timeline = dossier?.timeline || [];
  const [sel, setSel] = React.useState(null);
  if (!timeline.length) return <EmptyState icon={<FolderOpen size={22} />} title="No hourly data" description="No tracked hours in this window." />;

  const byDay = new Map();
  for (const h of timeline) {
    const day = String(h.hourKey || "").slice(0, 10);
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day).push({ ...h, hour: Number(String(h.hourKey).slice(11, 13)) });
  }

  return (
    <>
      <H3 t={t}>Hour by hour</H3>
      <Sub t={t}>
        Each block is one tracked hour, shaded by how much of it was tracked. Amber means idle ran over
        half the hour. Click a block for what it contained.
      </Sub>
      {[...byDay.entries()].map(([day, hours]) => (
        <div key={day} style={{ marginBottom: 16 }}>
          <div style={{
            display: "flex", alignItems: "baseline", justifyContent: "space-between",
            fontSize: 11, color: t.c.textTertiary, marginBottom: 6,
          }}>
            <span>{day}</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {fmtMins(hours.reduce((a, h) => a + (h.trackedMinutes || 0), 0))}
            </span>
          </div>
          <HourStrip hours={hours} onPick={setSel} selected={sel} />
        </div>
      ))}
      {sel && (
        <div style={{ marginTop: 14, padding: `${t.s[4]}px ${t.s[4]}px`, borderRadius: t.r.md, background: t.c.surfaceMuted, boxShadow: `0 0 0 1px ${t.c.border}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <SecLabel t={t}>{sel.label || sel.hourKey}</SecLabel>
            {sel.hasSummary === false && <Badge tone="neutral">no hourly analysis</Badge>}
          </div>
          {Array.isArray(sel.workSummaryBullets) && sel.workSummaryBullets.length > 0 ? (
            <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13, lineHeight: 1.6, color: t.c.textSecondary }}>
              {sel.workSummaryBullets.slice(0, 5).map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          ) : (
            <p style={{ margin: "8px 0 0", fontSize: 13, color: t.c.textTertiary, lineHeight: 1.6 }}>
              No written summary exists for this hour — the figures below come from activity density.
            </p>
          )}
          <KV t={t} items={[
            ["Tracked", fmtMins(sel.trackedMinutes)],
            ["Active", fmtMins(sel.activeMinutes)],
            ["Idle", fmtMins(sel.idleMinutes)],
          ]} />
        </div>
      )}
    </>
  );
}

function ProjectsTab({ t, dossier, related }) {
  // Prefer the dual-source analytics payload; fall back to the dossier's own
  // split (also dual-source since 1.2.0) if that request failed.
  const rows = related?.projectAnalytics?.length
    ? related.projectAnalytics.map((p) => ({
      id: p.projectId, name: p.projectName, time: p.formattedTime,
      sessions: p.source === "agent" ? null : p.sessions,
      source: p.source || "app",
      share: null,
    }))
    : (dossier?.projects || []).map((p) => ({
      id: p.projectId, name: p.name, time: fmtMins(p.trackedMinutes),
      sessions: p.source === "agent" ? null : p.sessionCount,
      source: p.source || "app",
      share: Math.round((Number(p.share) || 0) * 100),
    }));

  if (!rows.length) return <EmptyState icon={<FolderOpen size={22} />} title="No projects" description="No project activity in this window." />;

  return (
    <>
      <H3 t={t}>Projects</H3>
      <Sub t={t}>
        Attributed time per project across the whole window, so everyone gets a real breakdown
        regardless of how their activity was captured.
      </Sub>
      <Table t={t}
        head={["Project", "Sessions", ...(rows[0].share != null ? ["Share"] : []), "Time"]}
        rows={rows.map((r) => [
          r.name,
          r.sessions == null ? <span key="n" style={{ color: t.c.textTertiary }}>—</span> : r.sessions,
          ...(r.share != null ? [`${r.share}%`] : []),
          <b key="t">{r.time}</b>,
        ])}
      />
    </>
  );
}

function PeopleTab({ t, related, onPick }) {
  const rows = related?.memberHours || [];
  if (!rows.length) return <EmptyState icon={<FolderOpen size={22} />} title="No contributors" description="No tracked member time on this project." />;
  return (
    <>
      <H3 t={t}>People on this project</H3>
      <Sub t={t}>
        Tracked time per contributor. Where a member's time carries no session detail it reads
        “no sessions” rather than 0. Click anyone to see what they actually did on this project.
      </Sub>
      <ContributorList related={related} onPick={onPick} />

      {/* The raw table stays underneath for the figures the list summarises. */}
      <div style={{ marginTop: 28 }}>
        <SecLabel t={t}>The numbers</SecLabel>
      </div>
      <Table t={t}
        head={["Member", "Sessions", "Active", "Time"]}
        rows={rows.map((m) => [
          <span key="n" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar user={m} name={api.memberName(m)} size={26} />
            <span>
              <span style={{ display: "block", fontWeight: 500 }}>{api.memberName(m)}</span>
              {m.offTeamRoster && <span style={{ fontSize: 10, color: t.c.textTertiary }}>not on project roster</span>}
            </span>
          </span>,
          m.source === "agent" ? <span key="x" style={{ color: t.c.textTertiary }}>—</span> : (m.sessionCount || 0),
          m.totalActiveTimeFormatted || "0h 0m",
          <b key="t">{fmtMins(Math.round((Number(m.totalHours) || 0) * 60))}</b>,
        ])}
      />
    </>
  );
}

function SessionsTab({ t, dossier, subject }) {
  const sources = dossier?.metrics?.sources;
  const count = dossier?.metrics?.sessionCount || 0;

  if (sources?.primary === "agent") {
    return (
      <>
        <H3 t={t}>Session log</H3>
        <Sub t={t}>Start/stop sessions recorded for this window.</Sub>
        <SourceGap>
          <b>No sessions for this window.</b>{" "}
          {(subject.name || "This person").split(" ")[0]}’s {sources.agent?.formatted} this period was not
          captured as start/stop sessions. This is absent detail, not zero work — the figures in the Hours
          tab are real.
        </SourceGap>
      </>
    );
  }

  return (
    <>
      <H3 t={t}>Sessions</H3>
      <Sub t={t}>Only time captured as start/stop sessions appears here.</Sub>
      <KV t={t} items={[
        ["Sessions", count],
        ["Longest", fmtMins(dossier?.metrics?.longestSessionMinutes || 0)],
        ["First activity", dossier?.metrics?.firstActivityAt ? new Date(dossier.metrics.firstActivityAt).toLocaleString() : "—"],
        ["Last activity", dossier?.metrics?.lastActivityAt ? new Date(dossier.metrics.lastActivityAt).toLocaleString() : "—"],
      ]} />
      {sources?.sessionsCoverAllTime === false && (
        <div style={{ marginTop: t.s[5] }}>
          <SourceGap>
            These cover only {sources.app?.formatted} of the window. A further {sources.agent?.formatted}
            {" "}was tracked without session detail.
          </SourceGap>
        </div>
      )}
    </>
  );
}

/* ── small shared bits ───────────────────────────────────────────────────── */
const Dot = ({ c }) => <span style={{ width: 7, height: 7, borderRadius: 99, background: c, display: "inline-block", marginRight: 5 }} />;

function SecLabel({ t, children, hint }) {
  return (
    <div style={{
      display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase",
      color: t.c.textTertiary, marginBottom: 9,
    }}>
      <span>{children}</span>
      {hint && (
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, flexShrink: 0 }}>{hint}</span>
      )}
    </div>
  );
}
const H3 = ({ t, children }) => <h3 style={{ fontSize: t.font.size.lg, fontWeight: 600, margin: "0 0 3px", color: t.c.text }}>{children}</h3>;
const Sub = ({ t, children }) => <p style={{ fontSize: 12, color: t.c.textTertiary, margin: "0 0 16px", lineHeight: 1.55 }}>{children}</p>;

function KV({ t, items }) {
  return (
    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginTop: 10 }}>
      {items.map(([k, v], i) => (
        <div key={i} style={{ fontSize: 12, color: t.c.textTertiary }}>
          {k}
          <b style={{ display: "block", fontSize: 14, color: t.c.text, fontWeight: 600, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{v}</b>
        </div>
      ))}
    </div>
  );
}

function Table({ t, head, rows }) {
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i} style={{
              textAlign: i === 0 ? "left" : i === head.length - 1 ? "right" : "left",
              fontSize: 11, fontWeight: 600, color: t.c.textTertiary,
              padding: "7px 10px", borderBottom: `1px solid ${t.c.border}`,
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((cell, j) => (
              <td key={j} style={{
                padding: "9px 10px", borderBottom: `1px solid ${t.c.border}`,
                textAlign: j === 0 ? "left" : j === r.length - 1 ? "right" : "left",
                fontVariantNumeric: "tabular-nums", color: t.c.text,
              }}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function L1Skeleton({ t }) {
  return (
    <>
      <div style={{ padding: t.s[4], borderRadius: t.r.md, background: t.c.surfaceMuted }}>
        <Shimmer w="45%" h={10} /><div style={{ height: 10 }} /><Shimmer w="60%" h={24} />
        <div style={{ height: 14 }} /><Shimmer w="100%" h={6} />
      </div>
      <div style={{ padding: t.s[4], borderRadius: t.r.md, background: t.c.surfaceMuted }}>
        <Shimmer w="100%" h={12} /><div style={{ height: 7 }} />
        <Shimmer w="90%" h={12} /><div style={{ height: 7 }} />
        <Shimmer w="70%" h={12} />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ padding: t.s[3], borderRadius: t.r.md, background: t.c.surfaceMuted }}>
          <Shimmer w="70%" h={11} /><div style={{ height: 6 }} /><Shimmer w="95%" h={10} />
        </div>
      ))}
    </>
  );
}

function sevColor(t, sev) {
  if (sev === "negative") return t.status.negative;
  if (sev === "warning") return t.status.warning;
  if (sev === "positive") return t.status.positive;
  return t.status.info;
}

export default DrilldownDrawer;
